'use client';

import { useEffect } from 'react';

import { startAppEffects } from 'lib/stores/appEffects';

// Starts the app's global client-side effects (color palette bridge, session
// teardown, referral cookie, session restore) on mount and tears them down on
// unmount. Renders nothing and provides no context — it exists purely for its
// mount/unmount lifecycle, so it sits as a self-closing sibling rather than
// wrapping the tree.
//
// Ref-counted with a deferred (next-tick) teardown, mirroring the deleted
// RootStoreProvider's mountedProviderCount/scheduleStoreDispose: navigating
// between two route groups that each render their own `Providers` (e.g.
// `(main)` -> `(explore)`) unmounts the old instance and mounts a new one
// around the same commit. Without deferring, that transient gap would run
// `stop()` (killing the legacy WS client, resetting user-scoped query state)
// only to immediately restart it — a new instance mounting before the
// deferred timer fires cancels the pending stop instead.
let stopAppEffects: (() => void) | undefined;
let mountedCount = 0;
let disposeTimer: ReturnType<typeof setTimeout> | null = null;

function cancelScheduledStop(): void {
    if (disposeTimer === null) return;
    clearTimeout(disposeTimer);
    disposeTimer = null;
}

function scheduleStop(): void {
    cancelScheduledStop();
    disposeTimer = setTimeout(() => {
        disposeTimer = null;
        if (mountedCount > 0) return;
        stopAppEffects?.();
        stopAppEffects = undefined;
    }, 0);
}

export default function AppEffectsBootstrap(): null {
    useEffect(() => {
        cancelScheduledStop();
        mountedCount += 1;
        stopAppEffects ??= startAppEffects();

        return () => {
            mountedCount = Math.max(0, mountedCount - 1);
            if (mountedCount === 0) scheduleStop();
        };
    }, []);

    return null;
}
